import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { Bell, Send, Loader2, CheckCircle, AlertTriangle, Info, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAdminAxios = () => {
    const token = localStorage.getItem('admin_token');
    return axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });
};

export default function AdminNotifications() {
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [showSendForm, setShowSendForm] = useState(false);
    const [providers, setProviders] = useState([]);
    const [users, setUsers] = useState([]);
    
    const [form, setForm] = useState({
        recipient_type: 'provider',
        recipient_id: '',
        title: '',
        message: '',
        notification_type: 'info'
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [notifRes, providersRes, usersRes] = await Promise.all([
                getAdminAxios().get('/admin/notifications'),
                getAdminAxios().get('/admin/providers'),
                getAdminAxios().get('/admin/users')
            ]);
            setNotifications(notifRes.data);
            setProviders(providersRes.data);
            setUsers(usersRes.data);
        } catch (err) {
            if (err.response?.status === 401) {
                navigate('/admin/login');
            }
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    const handleSend = async () => {
        if (!form.title || !form.message) {
            toast.error('Please fill in title and message');
            return;
        }

        setSending(true);
        try {
            await getAdminAxios().post('/admin/notifications/send', {
                user_id: form.recipient_type === 'user' ? form.recipient_id : null,
                provider_id: form.recipient_type === 'provider' ? form.recipient_id : null,
                title: form.title,
                message: form.message,
                notification_type: form.notification_type
            });
            
            toast.success('Notification sent successfully!');
            setShowSendForm(false);
            setForm({ recipient_type: 'provider', recipient_id: '', title: '', message: '', notification_type: 'info' });
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to send notification');
        } finally {
            setSending(false);
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'success': return <CheckCircle className="w-4 h-4 text-green-500" />;
            case 'warning': return <AlertTriangle className="w-4 h-4 text-amber-500" />;
            case 'job_request': return <Briefcase className="w-4 h-4 text-blue-500" />;
            default: return <Info className="w-4 h-4 text-slate-400" />;
        }
    };

    const getTypeBadge = (type) => {
        const styles = {
            success: 'bg-green-500/20 text-green-500',
            warning: 'bg-amber-500/20 text-amber-500',
            job_request: 'bg-blue-500/20 text-blue-500',
            info: 'bg-slate-500/20 text-slate-400'
        };
        return styles[type] || styles.info;
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-white">Notifications</h1>
                    <p className="text-slate-400">Send and manage system notifications</p>
                </div>
                <Button 
                    onClick={() => setShowSendForm(true)}
                    className="bg-indigo-600 hover:bg-indigo-700"
                >
                    <Send className="w-4 h-4 mr-2" />
                    Send Notification
                </Button>
            </div>

            {/* Send Notification Form */}
            {showSendForm && (
                <Card className="bg-slate-800 border-slate-700 mb-6">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Bell className="w-5 h-5 text-indigo-500" />
                            Send New Notification
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300">Recipient Type</Label>
                                <Select 
                                    value={form.recipient_type} 
                                    onValueChange={(v) => setForm({...form, recipient_type: v, recipient_id: ''})}
                                >
                                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="provider">MediProvider</SelectItem>
                                        <SelectItem value="user">User</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300">Select Recipient</Label>
                                <Select 
                                    value={form.recipient_id} 
                                    onValueChange={(v) => setForm({...form, recipient_id: v})}
                                >
                                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                                        <SelectValue placeholder="Choose recipient..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {form.recipient_type === 'provider' 
                                            ? providers.map(p => (
                                                <SelectItem key={p.id} value={p.id}>{p.name} ({p.email})</SelectItem>
                                            ))
                                            : users.map(u => (
                                                <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                                            ))
                                        }
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-slate-300">Title</Label>
                                <Input 
                                    value={form.title}
                                    onChange={(e) => setForm({...form, title: e.target.value})}
                                    placeholder="Notification title..."
                                    className="bg-slate-700 border-slate-600 text-white"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-slate-300">Type</Label>
                                <Select 
                                    value={form.notification_type} 
                                    onValueChange={(v) => setForm({...form, notification_type: v})}
                                >
                                    <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="info">Info</SelectItem>
                                        <SelectItem value="success">Success</SelectItem>
                                        <SelectItem value="warning">Warning</SelectItem>
                                        <SelectItem value="job_request">Job Request</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-slate-300">Message</Label>
                            <Textarea 
                                value={form.message}
                                onChange={(e) => setForm({...form, message: e.target.value})}
                                placeholder="Notification message..."
                                className="bg-slate-700 border-slate-600 text-white"
                                rows={3}
                            />
                        </div>

                        <div className="flex gap-3">
                            <Button 
                                variant="outline"
                                className="border-slate-600 text-slate-300"
                                onClick={() => setShowSendForm(false)}
                            >
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleSend}
                                disabled={sending}
                                className="bg-indigo-600 hover:bg-indigo-700"
                            >
                                {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                                Send
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Notifications List */}
            {loading ? (
                <div className="flex justify-center py-12">
                    <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                </div>
            ) : notifications.length === 0 ? (
                <Card className="bg-slate-800 border-slate-700">
                    <CardContent className="py-12 text-center">
                        <Bell className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                        <p className="text-slate-400">No notifications yet</p>
                    </CardContent>
                </Card>
            ) : (
                <div className="space-y-3">
                    {notifications.map((notif) => (
                        <Card key={notif.id} className="bg-slate-800 border-slate-700">
                            <CardContent className="p-4">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-slate-700 flex items-center justify-center">
                                        {getTypeIcon(notif.notification_type)}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-medium text-white">{notif.title}</h3>
                                            <span className={`px-2 py-0.5 rounded-full text-xs ${getTypeBadge(notif.notification_type)}`}>
                                                {notif.notification_type}
                                            </span>
                                        </div>
                                        <p className="text-sm text-slate-400">{notif.message}</p>
                                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                                            <span>
                                                To: {notif.user_id ? 'User' : 'Provider'}
                                            </span>
                                            <span>
                                                {new Date(notif.created_at).toLocaleString()}
                                            </span>
                                            {notif.read && (
                                                <span className="text-green-500">Read</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    );
}
